import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  Alert,
} from 'react-native';
import { useRoute, RouteProp } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { schedulesApi } from '../../services/apiService';
import { KPIReadingScheduleList } from '../../types';
import { COLORS, LABELS } from '../../constants/config';
import { LoadingScreen, EmptyState, Badge, PageContainer, Navbar, Breadcrumb, ViewOnlyBadge } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useResponsive, useRoleNavigation } from '../../hooks';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';

import { tokens } from '@design';
type RouteProps = RouteProp<MainStackParamList, 'Schedules'>;

export default function SchedulesScreen() {
  const route = useRoute<RouteProps>();
  const organizationId = route.params?.organizationId;
  const { isMobile, isSmallMobile } = useResponsive();
  const { isViewOnly } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { selectedYear } = useYear();

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
    sectionBg: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
  };

  const [schedules, setSchedules] = useState<KPIReadingScheduleList[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'overdue' | 'completed'>('pending');

  const fetchSchedules = useCallback(async () => {
    try {
      setError(null);
      const params: Record<string, any> = {};
      if (organizationId) params.department_id = organizationId;
      if (filter === 'pending') params.is_completed = false;
      if (filter === 'completed') params.is_completed = true;
      if (filter === 'overdue') params.is_overdue = true;

      const response = await schedulesApi.list(params);
      setSchedules(response.results);
    } catch (err: any) {
      setError(err.message || t('schedules.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [organizationId, filter]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Refetch when year changes
  useEffect(() => {
    fetchSchedules();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchSchedules();
  };

  const handleMarkComplete = async (schedule: KPIReadingScheduleList) => {
    Alert.prompt(
      t('schedules.enterReading'),
      t('schedules.enterReadingValueFor', { name: schedule.kpi_name }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.save'),
          onPress: async (value) => {
            if (value) {
              try {
                await schedulesApi.markComplete(schedule.id, parseFloat(value));
                fetchSchedules();
                Alert.alert(t('common.success'), t('schedules.readingSavedSuccess'));
              } catch (err) {
                Alert.alert(t('common.error'), t('schedules.readingSaveFailed'));
              }
            }
          },
        },
      ],
      'plain-text'
    );
  };

  const renderItem = ({ item }: { item: KPIReadingScheduleList }) => {
    const today = new Date().toISOString().split('T')[0];
    const isOverdue = !item.is_completed && item.scheduled_date < today;

    return (
      <View style={[styles.card, isOverdue && styles.cardOverdue]}>
        <View style={styles.cardHeader}>
          <View style={[styles.statusIndicator, { backgroundColor: item.is_completed ? COLORS.success : isOverdue ? COLORS.danger : COLORS.warning }]} />
          <View style={styles.cardInfo}>
            <Text style={styles.kpiName}>{item.kpi_name}</Text>
            <Text style={styles.period}>{item.period_label}</Text>
          </View>
          {/* Hide mark complete button for view-only users (executive) */}
          {!item.is_completed && !isViewOnly && (
            <TouchableOpacity
              style={styles.completeButton}
              onPress={() => handleMarkComplete(item)}
            >
              <Ionicons name="checkmark-circle-outline" size={28} color={COLORS.success} />
            </TouchableOpacity>
          )}
        </View>
        <View style={styles.cardFooter}>
          <View style={styles.dateContainer}>
            <Ionicons name="calendar-outline" size={14} color={COLORS.textMuted} />
            <Text style={[styles.date, isOverdue && styles.dateOverdue]}>
              {item.scheduled_date}
            </Text>
          </View>
          {item.is_completed ? (
            <Badge text={t('schedules.completed')} variant="success" size="small" />
          ) : isOverdue ? (
            <Badge text={t('schedules.overdue')} variant="danger" size="small" />
          ) : (
            <Badge text={t('schedules.upcoming')} variant="warning" size="small" />
          )}
        </View>
      </View>
    );
  };

  const FilterButton = ({ value, label }: { value: typeof filter; label: string }) => (
    <TouchableOpacity
      style={[styles.filterButton, filter === value && styles.filterButtonActive]}
      onPress={() => setFilter(value)}
    >
      <Text style={[styles.filterButtonText, filter === value && styles.filterButtonTextActive]}>
        {label}
      </Text>
    </TouchableOpacity>
  );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('sidebar.dashboard') },
          { label: t('schedules.title') },
        ]}
      />
      {/* View-only badge for executive users */}
      {isViewOnly && (
        <View style={{ padding: tokens.spacing.absolute["4"], paddingBottom: tokens.spacing.absolute["0"] }}>
          <ViewOnlyBadge />
        </View>
      )}
      {/* Filters */}
      <View style={styles.filters}>
        <FilterButton value="pending" label={t('schedules.pending')} />
        <FilterButton value="overdue" label={t('schedules.overdueFilter')} />
        <FilterButton value="completed" label={t('schedules.completedFilter')} />
        <FilterButton value="all" label={t('common.all')} />
      </View>

      {error ? (
        <EmptyState
          icon="alert-circle-outline"
          title={LABELS.error}
          message={error}
          actionLabel={LABELS.refresh}
          onAction={fetchSchedules}
        />
      ) : schedules.length === 0 ? (
        <EmptyState
          icon="calendar-outline"
          title={LABELS.noData}
          message={t('schedules.noScheduledReadings')}
        />
      ) : (
        <FlatList
          data={schedules}
          renderItem={renderItem}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.list}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        />
      )}
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  filters: {
    flexDirection: 'row',
    padding: tokens.spacing.absolute["4"],
    backgroundColor: COLORS.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: COLORS.border,
  },
  filterButton: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    marginHorizontal: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-20"],
    backgroundColor: COLORS.light,
    alignItems: 'center',
  },
  filterButtonActive: {
    backgroundColor: COLORS.primary,
  },
  filterButtonText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.text,
  },
  filterButtonTextActive: {
    color: COLORS.white,
    fontWeight: '600',
  },
  list: {
    padding: tokens.spacing.absolute["4"],
  },
  card: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    marginBottom: tokens.spacing.absolute["3"],
    padding: tokens.spacing.absolute["4"],
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
    overflow: 'hidden',
  },
  cardOverdue: {
    borderEndWidth: 4,
    borderEndColor: COLORS.danger,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusIndicator: {
    width: 8,
    height: 8,
    borderRadius: tokens.radii.absolute["radius-4"],
    marginStart: tokens.spacing.absolute["3"],
  },
  cardInfo: {
    flex: 1,
    alignItems: 'flex-end',
  },
  kpiName: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    color: COLORS.text,
    textAlign: getTextAlign(isRTL),
  },
  period: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.textMuted,
    marginTop: tokens.spacing.absolute["1"],
  },
  completeButton: {
    padding: tokens.spacing.absolute["1"],
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: tokens.spacing.absolute["3"],
    paddingTop: tokens.spacing.absolute["3"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: COLORS.border,
  },
  dateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  date: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.textMuted,
    marginStart: tokens.spacing.absolute["1"],
  },
  dateOverdue: {
    color: COLORS.danger,
  },
});

const styles = getStyles(true);
